import React, { useCallback, useEffect } from 'react'
import { FlatList } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Stack } from '@zet/design-system'
import { KeyBoardWrapper } from '@zet/utils'
import { getLoginHash } from '@zet/utils/loginHash'
import { Banner } from '@zet/zet-pro-quick-journey'
import { useAddCustomerState } from '@zet/zet-pro-quick-journey/hooks/useAddCustomer'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import { useForm } from 'react-hook-form'

import { Button, ScreenTitle } from '../../components'
import FormController from '../../form/FormController'
import {
  sendAnalyticsData,
  sendJfsCustomEvent,
} from '../../utils/analyticsEvents'
import {
  getJfsCustomerId,
  getJfsCustomerPhoneNumber,
} from '../../utils/jfsSession'
import { customerInfoValidationSchema } from '../../utils/validationSchemas'
import { customerInfoFormFields } from './formFields'

export const CustomerInfo = () => {
  const { mutate, isLoading: isAddingCustomer } = useAddCustomerState()
  const { bannerImage } = useGetAgentDetailsAndTheme()
  const {
    setCustomerPhoneNumber,
    customerPhoneNumber,
    customerId: storeCustomerId,
  } = useAddCustomerStore()

  const {
    control,
    formState: { isValid },
    getValues,
    setValue,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      fullName: '',
      email: '',
    },
    resolver: zodResolver(customerInfoValidationSchema),
  })

  const renderItem = useCallback(({ item }) => {
    return <FormController control={control} ocField={item} />
  }, [])

  const handleSubmit = () => {
    const customerId = getJfsCustomerId()
    const { fullName, email, phoneNumber } = getValues()
    sendAnalyticsData({
      screen: 'SHOW_PRIMARY_DETAILS_SCREEN_JFS',
      type: 'click',
    })
    sendJfsCustomEvent('enter_user_details', {
      // No customer id exists before the first successful submit — the
      // hashed phone number becomes the identifier (handled centrally).
      customer_id: customerId || storeCustomerId || '',
      phone_number: phoneNumber,
      click_header: "Let's get to know you",
      click_text: 'Continue',
    })

    setCustomerPhoneNumber(phoneNumber)
    const hash = encodeURIComponent(getLoginHash() || '')
    const jfsConsent = sessionStorage.getItem('jfsConsent') as string
    const requestBodyData = {
      hash,
      email: email?.trim(),
      fullName: fullName?.trim(),
      phoneNumber: phoneNumber,
      ...(customerId && { customerId: Number(customerId) }),
      jfsConsent: JSON.parse(jfsConsent),
      screenName: 'SHOWN_PRIMARY_DETAILS_SCREEN_JFS',
    }
    mutate(requestBodyData, {
      onSuccess: (data) => {
        sendAnalyticsData({ screen: data?.action, type: 'screen' })
      },
    })
  }

  useEffect(() => {
    const phoneNumber = customerPhoneNumber || getJfsCustomerPhoneNumber()

    if (phoneNumber) {
      setValue('phoneNumber', phoneNumber)
    }
  }, [customerPhoneNumber, setValue])

  return (
    <KeyBoardWrapper
      BottomSheet={
        <Button
          disabled={!isValid || isAddingCustomer}
          onPress={handleSubmit}
          title={'Continue'}
          loading={isAddingCustomer}
        />
      }
    >
      <Stack className="flex-1 bg-white px-3">
        <Stack>
          <FlatList
            showsVerticalScrollIndicator={false}
            data={customerInfoFormFields}
            nestedScrollEnabled
            ListHeaderComponent={
              <ScreenTitle
                title={"Let's get to know you"}
                subTitle={'Please provide your details to get started.'}
              />
            }
            bounces={false}
            renderItem={renderItem}
          />
        </Stack>
      </Stack>
    </KeyBoardWrapper>
  )
}
